import { getCatalogRamSnapshotCache } from './catalogRamSnapshotCache';

export type RemoteSnapshotProductRow = {
  id: string;
  name: string;
  price: number;
  sku: string | null;
  barcode: string | null;
  category_id: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  item_type?: string | null;
  is_misc?: boolean | null;
  organization_id?: string | null;
  brand?: string | null;
  brand_id?: string | null;
  internal_notes?: string | null;
  preferred_supplier_id?: string | null;
  supplier_item_code?: string | null;
  unit_of_measure?: string | null;
  sell_by_weight?: boolean | null;
  duration_minutes?: number | null;
  buffer_before_minutes?: number | null;
  buffer_after_minutes?: number | null;
  parent_product_id?: string | null;
  image_url?: string | null;
  video_url?: string | null;
  booking_mode?: string | null;
  kitchen_destination?: string | null;
  prep_minutes?: number | null;
  combo_valid_from?: string | null;
  combo_valid_until?: string | null;
  menu_available?: boolean | null;
  visible_on_menu_carta?: boolean | null;
  is_public?: boolean | null;
};

export type RemoteSnapshotStockRow = {
  product_id: string;
  branch_id: string;
  quantity: number;
  min_quantity: number;
  avg_cost?: number | null;
  /** Null = stock a nivel producto (parent). Con variantes, no sumar filas de variant. */
  variant_id?: string | null;
};

/** Carga snapshot web (cache en memoria). No bloquea esperando sync remoto. */
export async function loadWebCatalogSnapshotRows(): Promise<{
  products: RemoteSnapshotProductRow[];
  stocks: RemoteSnapshotStockRow[];
} | null> {
  const cachedSnapshot = getCatalogRamSnapshotCache();
  if (cachedSnapshot && cachedSnapshot.products.length > 0) {
    return {
      products: cachedSnapshot.products as RemoteSnapshotProductRow[],
      stocks: cachedSnapshot.stocks as RemoteSnapshotStockRow[],
    };
  }
  return null;
}

/** Agrega filas de stock por producto en una sucursal.
 * Prioriza filas parent (`variant_id` null). Si no hay parent, suma variantes.
 * Evita doble-contar rollup parent + filas de variante.
 */
export function aggregateRemoteStocksByProduct(
  branchId: string,
  stocks: RemoteSnapshotStockRow[],
  options?: { weightedAvgCost?: boolean }
): Map<string, RemoteSnapshotStockRow> {
  const stockByProduct = new Map<string, RemoteSnapshotStockRow>();
  const weightedAvgCost = options?.weightedAvgCost === true;

  const branchStocks = stocks.filter((stock) => stock.branch_id === branchId);
  const parentRows = branchStocks.filter((s) => s.variant_id == null || s.variant_id === '');
  const hasParentByProduct = new Set(parentRows.map((s) => s.product_id));
  const rowsToAggregate = branchStocks.filter((s) => {
    if (s.variant_id == null || s.variant_id === '') return true;
    return !hasParentByProduct.has(s.product_id);
  });

  for (const stock of rowsToAggregate) {
    const existing = stockByProduct.get(stock.product_id);
    const qty = Number(stock.quantity ?? 0);
    const minQty = Number(stock.min_quantity ?? 0);

    if (!existing) {
      stockByProduct.set(stock.product_id, {
        ...stock,
        quantity: qty,
        min_quantity: minQty,
        avg_cost: weightedAvgCost ? Number(stock.avg_cost ?? 0) : undefined,
        variant_id: null,
      });
      continue;
    }

    const existingQty = Number(existing.quantity ?? 0);
    const nextQty = existingQty + qty;
    let nextAvg = existing.avg_cost;
    if (weightedAvgCost) {
      const existingAvg = Number(existing.avg_cost ?? 0);
      const avgCost = Number(stock.avg_cost ?? 0);
      nextAvg = nextQty > 0 ? (existingQty * existingAvg + qty * avgCost) / nextQty : 0;
    }

    stockByProduct.set(stock.product_id, {
      ...existing,
      quantity: nextQty,
      min_quantity: Math.max(Number(existing.min_quantity ?? 0), minQty),
      avg_cost: nextAvg,
      variant_id: null,
    });
  }

  return stockByProduct;
}
